import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Grid,
  Card,
  CardContent,
  Button,
  CircularProgress,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Divider,
  Link,
  Tooltip,
  IconButton,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Language as LanguageIcon,
  Info as InfoIcon,
  Insights as InsightsIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import { authService } from '../services/authService';
import { adInsightsService } from '../services/adInsightsService';
import { ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip as RechartsTooltip, CartesianGrid } from 'recharts';
import { tokens } from '../theme/tokens';

const OFF_FACEBOOK_ACTIVITY_HUB = 'https://www.facebook.com/off_facebook_activity';

/**
 * Facebook does not expose per-source deep links for off-activity.
 * Best UX: copy the source name to clipboard so the user can paste it
 * into the search box on the Facebook off-activity page.
 */
const openFacebookAndCopy = (sourceName) => {
  if (navigator.clipboard) {
    navigator.clipboard.writeText(sourceName).catch(() => {});
  }
  window.open(OFF_FACEBOOK_ACTIVITY_HUB, '_blank', 'noopener,noreferrer');
};

const AdInsightsPage = () => {
  const navigate = useNavigate();
  const [insights, setInsights] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadInsights();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadInsights = async () => {
    try {
      setLoading(true);
      setError(null);
      const userData = await authService.getCurrentUser();
      const username = userData?.username || userData?.facebook_name || userData?.reddit_username;
      const userId = userData?.id;
      if (!username && !userId) {
        setError('Username not found. Please log in again.');
        setLoading(false);
        return;
      }
      const result = await adInsightsService.getAdInsights({ username, userId });
      if (result.status === 'success') {
        setInsights(result.data);
      } else {
        setError(result.message || 'Failed to load ad insights');
      }
    } catch (err) {
      console.error('Load ad insights error:', err);
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate('/login');
      } else {
        setError('Failed to load ad insights data.');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress size={56} thickness={4} sx={{ color: 'primary.main' }} />
        <Typography variant="h6" sx={{ mt: 3, color: 'text.secondary', fontWeight: 500 }}>
          Loading ad insights...
        </Typography>
      </Container>
    );
  }

  const hasAdvertisers = insights?.total_advertisers > 0;
  const hasOffFb = insights?.total_off_facebook > 0;
  const fbOffMeta = insights?.fb_off_meta_technologies || [];
  const fbConn = insights?.fb_connected_apps || [];
  const fbPerms = insights?.fb_app_permissions || [];
  const fbApps = insights?.fb_your_apps || [];
  const hasAppsSection = fbOffMeta.length + fbConn.length + fbPerms.length + fbApps.length > 0;
  const hasAnyData = hasAdvertisers || hasOffFb || hasAppsSection;

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 3, md: 4 }, pb: 6 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          p: { xs: 2.5, sm: 3.5 },
          mb: 3,
          borderRadius: `${tokens.radius.card}px`,
          background: tokens.hero.gradient,
          border: tokens.hero.border,
          boxShadow: tokens.hero.shadow,
        }}
      >
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={1.5}>
          <Stack direction="row" spacing={2} alignItems="center">
            <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(255,255,255,0.1)', display: 'flex', border: `1px solid ${tokens.border.subtle}` }}>
              <InsightsIcon sx={{ color: tokens.text.primary, fontSize: 26 }} />
            </Box>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 700, color: tokens.text.primary, letterSpacing: '-0.02em' }}>
                Ad Insights
              </Typography>
              <Typography variant="body2" sx={{ color: tokens.text.secondary, mt: 0.25 }}>
                Who has your data and who is tracking you across the web
              </Typography>
            </Box>
          </Stack>
          <Stack direction="row" spacing={1.5}>
            <Button
              variant="outlined"
              size="small"
              onClick={loadInsights}
              sx={{
                color: tokens.text.primary,
                borderColor: tokens.border.strong,
                '&:hover': { borderColor: tokens.accent.cyan, bgcolor: 'rgba(34, 211, 238, 0.08)' },
              }}
            >
              Refresh
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => navigate('/dashboard')}
              sx={{ bgcolor: tokens.accent.brandBlue, color: '#fff', fontWeight: 600, '&:hover': { bgcolor: '#1D4ED8' } }}
            >
              Back to Dashboard
            </Button>
          </Stack>
        </Stack>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 3 }}>{error}</Alert>}

      {!hasAnyData && !error && (
        <Paper
          elevation={0}
          sx={{ p: 4, textAlign: 'center', border: '1px solid', borderColor: 'divider', borderRadius: 3 }}
        >
          <InfoIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" gutterBottom sx={{ color: 'text.primary' }}>
            No ad insights uploaded yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Upload your full <strong>Download Your Information</strong> ZIP on the Facebook page.
          </Typography>
          <Stack spacing={1} alignItems="center">
            <Typography variant="body2">
              <strong>Advertisers (contact lists):</strong>{' '}
              <code>ads_information/advertisers_who_uploaded_a_contact_list.json</code>
            </Typography>
            <Typography variant="body2">
              <strong>Off-Facebook Activity:</strong>{' '}
              <code>off_facebook_activity/off_facebook_activity.json</code>
            </Typography>
          </Stack>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 3 }}>
            <Button variant="contained" onClick={() => navigate('/facebook')}>
              Facebook &amp; DYI upload
            </Button>
            <Button variant="outlined" onClick={() => navigate('/dashboard')}>
              Dashboard
            </Button>
          </Stack>
        </Paper>
      )}

      {hasAnyData && (
        <>
          {/* Summary stat cards */}
          {(hasAdvertisers || hasOffFb) && (
            <>
              <Grid container spacing={2.5} sx={{ mb: 3 }}>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(245,158,11,0.14)', display: 'flex' }}>
                          <BusinessIcon sx={{ color: tokens.accent.warning, fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" sx={{ color: tokens.accent.warning, fontWeight: 800 }}>
                            {insights.total_advertisers}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Advertisers with your contact info
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Card>
                    <CardContent>
                      <Stack direction="row" spacing={2} alignItems="center">
                        <Box sx={{ p: 1.25, borderRadius: 2, bgcolor: 'rgba(239,68,68,0.14)', display: 'flex' }}>
                          <LanguageIcon sx={{ color: tokens.accent.danger, fontSize: 24 }} />
                        </Box>
                        <Box>
                          <Typography variant="h3" sx={{ color: tokens.accent.danger, fontWeight: 800 }}>
                            {insights.total_off_facebook}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            Websites/apps sharing your data with Facebook
                          </Typography>
                        </Box>
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              <Alert severity="warning" sx={{ mb: 3 }}>
                <strong>What does this mean?</strong> Advertisers upload customer lists to Facebook to show you targeted ads.
                Off-Facebook activity means these websites sent your browsing data to Facebook so you can be tracked across the internet.
              </Alert>
            </>
          )}

          {hasOffFb && Array.isArray(insights.off_facebook_websites) && insights.off_facebook_websites.length > 0 && (
            <Paper
              elevation={0}
              sx={{
                mb: 3,
                p: 2.5,
                border: `1px solid ${tokens.border.subtle}`,
                borderRadius: `${tokens.radius.card}px`,
                bgcolor: tokens.bg.cardElevated,
              }}
            >
              <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 700, color: 'text.primary' }}>
                Off-Facebook activity (top sources by events)
              </Typography>
              <Box sx={{ width: '100%', height: 300 }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={insights.off_facebook_websites.slice(0, 12).map((s) => ({
                      name:
                        s.name && String(s.name).length > 24
                          ? `${String(s.name).slice(0, 22)}…`
                          : s.name || '—',
                      events: Number(s.event_count) || 0,
                    }))}
                    margin={{ top: 6, right: 12, left: 4, bottom: 52 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" stroke={tokens.chart.grid} vertical={false} />
                    <XAxis
                      dataKey="name"
                      tick={{ fill: tokens.chart.axis, fontSize: 10 }}
                      interval={0}
                      angle={-32}
                      textAnchor="end"
                      height={72}
                    />
                    <YAxis tick={{ fill: tokens.chart.axis, fontSize: 11 }} allowDecimals={false} />
                    <RechartsTooltip
                      cursor={{ fill: 'rgba(34, 211, 238, 0.06)' }}
                      contentStyle={{
                        backgroundColor: tokens.chart.tooltipBg,
                        border: `1px solid ${tokens.chart.tooltipBorder}`,
                        borderRadius: 10,
                      }}
                      labelStyle={{ color: tokens.chart.tooltipLabel, fontWeight: 600 }}
                    />
                    <Bar dataKey="events" fill={tokens.chart.barPrimary} radius={[4, 4, 0, 0]} maxBarSize={52} />
                  </BarChart>
                </ResponsiveContainer>
              </Box>
            </Paper>
          )}

          {/* Off-Facebook Activity Table */}
          {hasOffFb && (
            <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
              <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                <Stack direction="row" spacing={1.5} alignItems="center">
                  <LanguageIcon color="error" />
                  <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>
                    Websites &amp; Apps Tracking You for Facebook
                  </Typography>
                  <Chip label={insights.total_off_facebook} color="error" size="small" />
                </Stack>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  These websites/apps sent your activity (purchases, sign-ups, page visits) to Facebook. Sorted by number of events.
                </Typography>
              </Box>
              <TableContainer sx={{ maxHeight: 400 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>#</TableCell>
                      <TableCell>Website / App</TableCell>
                      <TableCell align="right">Events Sent</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {insights.off_facebook_websites.map((site, idx) => (
                      <TableRow key={idx} hover>
                        <TableCell>{idx + 1}</TableCell>
                        <TableCell>{site.name}</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={site.event_count}
                            size="small"
                            color={site.event_count > 10 ? 'error' : site.event_count > 3 ? 'warning' : 'default'}
                          />
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          )}

          {/* Apps and off-Meta section */}
          {hasAppsSection && (
            <>
              <Divider sx={{ my: 3 }} />
              <Typography variant="h6" gutterBottom sx={{ mb: 2, color: 'text.primary', fontWeight: 700 }}>
                Apps and off-Meta activity (from your DYI ZIP)
              </Typography>

              {fbOffMeta.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Off-Meta activity</Typography>
                      <Chip label={fbOffMeta.length} color="error" size="small" />
                    </Stack>
                  </Box>
                  <Alert severity="info" sx={{ m: 2 }} variant="outlined">
                    Click <strong>Manage</strong> next to any source — the source name is automatically copied to your clipboard.
                    Paste it into the search box on Facebook's Off-Activity page to find and disconnect that specific source.
                  </Alert>
                  <TableContainer sx={{ maxHeight: 360 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Source</TableCell>
                          <TableCell align="right">Events</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fbOffMeta.map((row, idx) => {
                          const rawUrl = row.url && String(row.url).trim();
                          const hasHttp = rawUrl && /^https?:\/\//i.test(rawUrl);
                          return (
                            <TableRow key={idx} hover>
                              <TableCell>{idx + 1}</TableCell>
                              <TableCell>
                                <Stack direction="row" alignItems="center" spacing={1}>
                                  {hasHttp ? (
                                    <Link href={rawUrl} target="_blank" rel="noopener noreferrer">
                                      {row.name}
                                    </Link>
                                  ) : (
                                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                      {row.name}
                                    </Typography>
                                  )}
                                  <Tooltip
                                    title={`Opens Facebook's Off-Activity page — "${row.name}" will be copied to your clipboard so you can paste it in the search box`}
                                    arrow
                                  >
                                    <IconButton
                                      size="small"
                                      onClick={() => openFacebookAndCopy(row.name)}
                                      sx={{
                                        color: 'primary.main',
                                        fontSize: '0.75rem',
                                        px: 0.75,
                                        py: 0.25,
                                        borderRadius: 1,
                                        border: '1px solid',
                                        borderColor: 'primary.main',
                                        '&:hover': { bgcolor: 'rgba(24,184,154,0.1)' },
                                      }}
                                    >
                                      <CopyIcon sx={{ fontSize: 13, mr: 0.4 }} />
                                      <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 600 }}>
                                        Manage
                                      </Typography>
                                    </IconButton>
                                  </Tooltip>
                                </Stack>
                              </TableCell>
                              <TableCell align="right">
                                <Chip label={row.event_count} size="small" />
                              </TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {fbConn.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Connected apps and websites</Typography>
                      <Chip label={fbConn.length} size="small" />
                    </Stack>
                  </Box>
                  <TableContainer sx={{ maxHeight: 280 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell>Link</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fbConn.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.name}</TableCell>
                            <TableCell sx={{ wordBreak: 'break-all' }}>{row.url || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {fbPerms.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Permissions you granted to apps</Typography>
                      <Chip label={fbPerms.length} size="small" />
                    </Stack>
                  </Box>
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>App</TableCell>
                          <TableCell>Permissions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fbPerms.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row.app}</TableCell>
                            <TableCell>{(row.permissions || []).join('; ') || '—'}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}

              {fbApps.length > 0 && (
                <Paper elevation={0} sx={{ mb: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
                  <Box sx={{ p: 2.5, borderBottom: 1, borderColor: 'divider' }}>
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 700 }}>Your apps</Typography>
                      <Chip label={fbApps.length} size="small" />
                    </Stack>
                  </Box>
                  <TableContainer>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>#</TableCell>
                          <TableCell>App</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {fbApps.map((row, idx) => (
                          <TableRow key={idx} hover>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell>{row}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Paper>
              )}
            </>
          )}

          <Divider sx={{ my: 2 }} />
          <Paper elevation={0} sx={{ p: 3, border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
            <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 700 }}>
              What You Can Do
            </Typography>
            <Stack spacing={1}>
              <Typography variant="body2">
                1. <strong>Manage Off-Facebook Activity:</strong> Go to Facebook → Settings → Your Facebook Information → Off-Facebook Activity → Clear History → Disconnect Future Activity
              </Typography>
              <Typography variant="body2">
                2. <strong>Control Ad Preferences:</strong> Facebook → Settings → Ads → Ad Settings → turn off "Data from partners"
              </Typography>
              <Typography variant="body2">
                3. <strong>Review each advertiser:</strong> Contact advertisers directly to request data deletion under GDPR/CCPA rights.
              </Typography>
            </Stack>
          </Paper>
        </>
      )}
    </Container>
  );
};

export default AdInsightsPage;
